import { DocumentEntity } from './entity';
import { SyncStatus } from 'shared/domain/entitySyncStatus/syncStatus';
import { DocumentDrawing } from 'shared/dtos/in/document';
import { DocumentTypes } from 'shared/types/document';
import { UploadStatus } from 'shared/types/uploadStatus';

export function buildDocumentMergePayload(
  apiDocument: Record<string, unknown>
): Partial<DocumentEntity> {
  const payload: Partial<DocumentEntity> = {};
  const drawings = Array.isArray(apiDocument.drawings) ? apiDocument.drawings : undefined;

  if (apiDocument.description !== undefined) {
    payload.description = apiDocument.description as string | null;
  }
  if (apiDocument.isExportAllowed !== undefined) {
    payload.isExportAllowed = Boolean(apiDocument.isExportAllowed);
  }
  if (apiDocument.imageRotation !== undefined) {
    const imageRotation =
      typeof apiDocument.imageRotation === 'number'
        ? apiDocument.imageRotation
        : Number(apiDocument.imageRotation);
    if (imageRotation !== undefined) {
      if (Number.isFinite(imageRotation)) {
        payload.imageRotation = imageRotation as DocumentEntity['imageRotation'];
      }
    }
  }
  if (apiDocument.deleted !== undefined) {
    payload.deleted = Boolean(apiDocument.deleted);
  }
  if (apiDocument.modifiedAt !== undefined && typeof apiDocument.modifiedAt === 'string') {
    payload.modifiedAt = apiDocument.modifiedAt;
  }
  if (apiDocument.imageConvertStatus !== undefined) {
    payload.imageConvertStatus = apiDocument.imageConvertStatus as DocumentEntity['imageConvertStatus'];
  }
  if (apiDocument.drawingSyncStatus !== undefined) {
    payload.drawingSyncStatus = apiDocument.drawingSyncStatus as SyncStatus;
  } else if (drawings && drawings.length > 0) {
    const latest = drawings[drawings.length - 1] as Record<string, unknown>;
    const merged = latest.mergedUploadStatus as string | undefined;
    if (merged === 'SUCCESS') {
      payload.drawingSyncStatus = SyncStatus.SUCCESS;
    } else if (merged === 'FAIL' || merged === 'PENDING') {
      payload.drawingSyncStatus = merged as SyncStatus;
    }
  }
  if (drawings) {
    payload.drawings = drawings as DocumentDrawing[];
  }

  const optStr = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
  const name = optStr(apiDocument.name);
  const fileType = optStr(apiDocument.fileType);
  const extension = optStr(apiDocument.extension);
  const url = optStr(apiDocument.url);
  const key = optStr(apiDocument.key);
  const bucketName = optStr(apiDocument.bucketName);
  const uploadStatusRaw = apiDocument.uploadStatus as UploadStatus | undefined;
  const drawingSrc = optStr(apiDocument.drawingSrc);
  const mergedSrc = optStr(apiDocument.mergedSrc);
  const thumbnailMergedSrc = optStr(apiDocument.thumbnailMergedSrc);
  const isDrawn = drawings !== undefined && drawings.length > 0;

  const hasAnyData =
    name !== undefined ||
    extension !== undefined ||
    url !== undefined ||
    key !== undefined ||
    bucketName !== undefined ||
    fileType !== undefined ||
    uploadStatusRaw !== undefined ||
    isDrawn ||
    drawingSrc !== undefined ||
    mergedSrc !== undefined ||
    thumbnailMergedSrc !== undefined;

  if (hasAnyData) {
    const merged: Partial<DocumentEntity['data']> = {};
    if (name !== undefined) merged.name = name;
    if (fileType !== undefined) merged.type = fileType;
    if (apiDocument.documentType !== undefined) merged.documentType = apiDocument.documentType as DocumentTypes;
    if (key !== undefined) merged.key = key;
    if (extension !== undefined) merged.extension = extension;
    if (url !== undefined) merged.url = url;
    if (bucketName !== undefined) merged.bucketName = bucketName;
    if (uploadStatusRaw !== undefined) merged.uploadStatus = uploadStatusRaw;
    merged.isDrawn = isDrawn;
    if (drawingSrc !== undefined) merged.drawingSrc = drawingSrc;
    if (mergedSrc !== undefined) merged.mergedSrc = mergedSrc;
    if (thumbnailMergedSrc !== undefined) merged.thumbnailMergedSrc = thumbnailMergedSrc;
    payload.data = merged as DocumentEntity['data'];
  }

  return payload;
}
